import {
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useCurrency } from "../../context/CurrencyContext";
import { usePortfolio } from "../../context/PortfolioContext";
import { percentage } from "../../shared/percentage";
import { ReserveGroup } from "../../shared/PortfolioTypes";
import {
  outstandingOf,
  RESERVE_HEADS,
  reserveTotal,
} from "../../shared/reserve";
import { Metric } from "../general/DataAtoms";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { card, cardRow, SectionHeading } from "./PortfolioSection";

const amount = { ...TABULAR_NUMERALS, whiteSpace: "nowrap" } as const;

/** The figures of a group of claims: paid, outstanding, incurred, per claim. */
const groupFigures = (group: ReserveGroup) => {
  const incurred = reserveTotal(group.total);
  return {
    paid: reserveTotal(group.paid),
    outstanding: incurred - reserveTotal(group.paid),
    incurred,
    perClaim: group.claims > 0 ? incurred / group.claims : 0,
    indemnityPerClaim:
      group.claims > 0 ? group.total.indemnity / group.claims : 0,
    // the defence costs against the indemnity; undefined without indemnity
    defenceToIndemnity:
      group.total.indemnity > 0
        ? group.total.defenceCosts / group.total.indemnity
        : undefined,
  };
};

/** A table that scrolls on its own where the page is too narrow for it. */
const Wide = ({ children }: { children: ReactNode }) => (
  <Box sx={{ overflowX: "auto", mx: -2.5 }}>
    <Table size="small" sx={{ "& td, & th": { px: 2.5 } }}>
      {children}
    </Table>
  </Box>
);

/**
 * What the claims of an insurer cost: paid, outstanding and incurred, by head,
 * by class of business and by kind of claim, the range of what is outstanding
 * across them all, and the dearest claims.
 */
export const ClaimsPortfolio = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "portfolio.claims" });
  const { t: tRoot } = useTranslation();
  const { currency } = useCurrency();
  const { reserves } = usePortfolio();
  const all = groupFigures(reserves.all);

  if (reserves.all.claims === 0)
    return (
      <Typography sx={{ color: "text.secondary" }}>{t("none")}</Typography>
    );

  const groupRows = (
    groups: (ReserveGroup & { label: string; key: string })[],
  ) => (
    <Wide>
      <TableHead>
        <TableRow>
          <TableCell>{t("columns.group")}</TableCell>
          {(
            [
              "claims",
              "paid",
              "outstanding",
              "incurred",
              "perClaim",
              "indemnityPerClaim",
              "defenceToIndemnity",
            ] as const
          ).map((column) => (
            <TableCell key={column} align="right">
              {t(`columns.${column}`)}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {[
          ...groups,
          { ...reserves.all, label: t("allClaims"), key: "all" },
        ].map(({ label, key, ...group }) => {
          const figures = groupFigures(group);
          return (
            <TableRow
              key={key}
              sx={
                key === "all"
                  ? { "& td, & th": { fontWeight: 700 } }
                  : undefined
              }
            >
              <TableCell component="th" scope="row">
                {label}
              </TableCell>
              <TableCell align="right" sx={amount}>
                {group.claims}
              </TableCell>
              {(
                [
                  "paid",
                  "outstanding",
                  "incurred",
                  "perClaim",
                  "indemnityPerClaim",
                ] as const
              ).map((column) => (
                <TableCell key={column} align="right" sx={amount}>
                  {currency.format(figures[column])}
                </TableCell>
              ))}
              <TableCell align="right" sx={amount}>
                {figures.defenceToIndemnity === undefined
                  ? "–"
                  : percentage.format(figures.defenceToIndemnity)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Wide>
  );

  return (
    <>
      <SectionHeading id="reserves">{t("reserves.title")}</SectionHeading>
      <Paper component="section" variant="outlined" sx={card}>
        <SectionLabel component="h3">{t("reserves.totals")}</SectionLabel>
        <Box
          sx={{
            ...cardRow,
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            mt: 1.75,
          }}
        >
          <Metric label={t("columns.paid")} value={all.paid} />
          <Metric label={t("columns.outstanding")} value={all.outstanding} />
          <Metric
            label={t("columns.incurred")}
            value={all.incurred}
            caption={t("reserves.incurredCaption")}
          />
          <Metric
            label={t("columns.perClaim")}
            value={all.perClaim}
            caption={t("reserves.across", { count: reserves.all.claims })}
          />
        </Box>
        <Wide>
          <TableHead>
            <TableRow>
              <TableCell>{t("columns.head")}</TableCell>
              <TableCell align="right">{t("columns.paid")}</TableCell>
              <TableCell align="right">{t("columns.outstanding")}</TableCell>
              <TableCell align="right">{t("columns.incurred")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {RESERVE_HEADS.map((head) => (
              <TableRow key={head}>
                <TableCell component="th" scope="row">
                  {tRoot(`reserve.heads.${head}`)}
                </TableCell>
                {[
                  reserves.all.paid,
                  outstandingOf(reserves.all.total, reserves.all.paid),
                  reserves.all.total,
                ].map((heads, index) => (
                  <TableCell key={index} align="right" sx={amount}>
                    {currency.format(heads[head])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Wide>
      </Paper>

      <SectionHeading id="classes" rule>
        {t("classes.title")}
      </SectionHeading>
      <Paper component="section" variant="outlined" sx={card}>
        <SectionLabel component="h3">{t("classes.byClass")}</SectionLabel>
        <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
          {t("classes.note")}
        </Typography>
        {groupRows(
          reserves.byClass.map((group) => ({
            ...group,
            key: group.claimType,
            label: tRoot(`home.claimTypes.${group.claimType}`, group.claimType),
          })),
        )}
      </Paper>
      <Paper component="section" variant="outlined" sx={card}>
        <SectionLabel component="h3">{t("classes.byKind")}</SectionLabel>
        {groupRows(
          reserves.byKind.map((group) => ({
            ...group,
            key: group.kind,
            label: tRoot(`case.claimKind.${group.kind}`),
          })),
        )}
      </Paper>

      <SectionHeading id="range" rule>
        {t("range.title")}
      </SectionHeading>
      <Paper component="section" variant="outlined" sx={card}>
        <SectionLabel component="h3">{t("range.outstanding")}</SectionLabel>
        <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
          {t("range.description")}
        </Typography>
        {reserves.outstandingRange ? (
          <Box
            sx={{
              ...cardRow,
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              mt: 1.75,
            }}
          >
            <Metric label={t("range.mean")} value={all.outstanding} />
            {reserves.outstandingRange.map((value, index) => (
              <Metric
                key={index}
                label={t(`range.chances.${index}`)}
                value={value}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ mt: 1.75 }}>
            {t("range.none")}
          </Typography>
        )}
      </Paper>

      <SectionHeading id="largest" rule>
        {t("largest.title")}
      </SectionHeading>
      <Paper component="section" variant="outlined" sx={card}>
        <Wide>
          <TableHead>
            <TableRow>
              <TableCell>{t("largest.claim")}</TableCell>
              <TableCell>{t("largest.kind")}</TableCell>
              <TableCell align="right">{t("columns.paid")}</TableCell>
              <TableCell align="right">{t("columns.outstanding")}</TableCell>
              <TableCell align="right">{t("columns.incurred")}</TableCell>
              <TableCell align="right">P90</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reserves.largest.map((claim) => {
              const incurred = reserveTotal(claim.total);
              return (
                <TableRow key={claim.caseId}>
                  <TableCell component="th" scope="row">
                    <Link
                      component={RouterLink}
                      to={`/cases/${claim.caseId}`}
                      underline="hover"
                      sx={{ fontWeight: 600 }}
                    >
                      {claim.caseName}
                    </Link>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ color: "text.secondary" }}
                    >
                      {[
                        ...claim.claimTypes.map((type) =>
                          tRoot(`home.claimTypes.${type}`, type),
                        ),
                        claim.insured,
                      ].join(" · ")}
                    </Typography>
                  </TableCell>
                  <TableCell>{tRoot(`case.claimKind.${claim.kind}`)}</TableCell>
                  <TableCell align="right" sx={amount}>
                    {currency.format(reserveTotal(claim.paid))}
                  </TableCell>
                  <TableCell align="right" sx={amount}>
                    {currency.format(incurred - reserveTotal(claim.paid))}
                  </TableCell>
                  <TableCell align="right" sx={{ ...amount, fontWeight: 600 }}>
                    {currency.format(incurred)}
                  </TableCell>
                  <TableCell align="right" sx={amount}>
                    {claim.p90 === undefined ? "–" : currency.format(claim.p90)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Wide>
      </Paper>
    </>
  );
};
